import React from 'react';
import type { HorsePerformance } from '../../../../types/horseProfile';

interface PerformanceEditFormProps {
  initialData: HorsePerformance;
  onSubmit: (data: HorsePerformance) => Promise<void>;
}

const DEFAULT_PERFORMANCE_DATA: HorsePerformance = {
  trainingLevel: '',
  disciplines: [],
  competitions: [],
  goals: [],
  trainer: '',
  trainingSchedule: ''
};

export const PerformanceEditForm: React.FC<PerformanceEditFormProps> = ({
  initialData = DEFAULT_PERFORMANCE_DATA,
  onSubmit
}) => {
  const [formData, setFormData] = React.useState<HorsePerformance>({
    ...DEFAULT_PERFORMANCE_DATA,
    ...initialData
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const addCompetition = () => {
    setFormData(prev => ({
      ...prev,
      competitions: [
        ...prev.competitions,
        {
          date: new Date().toISOString().split('T')[0],
          name: '',
          discipline: '',
          level: '',
          result: ''
        }
      ]
    }));
  };

  const removeCompetition = (index: number) => {
    setFormData(prev => ({
      ...prev,
      competitions: prev.competitions.filter((_, i) => i !== index)
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Ausbildungsstand
        </label>
        <input
          type="text"
          value={formData.trainingLevel}
          onChange={(e) => setFormData({ ...formData, trainingLevel: e.target.value })}
          className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Disziplinen
        </label>
        <input
          type="text"
          value={formData.disciplines.join(', ')}
          onChange={(e) => setFormData({
            ...formData,
            disciplines: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
          })}
          className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
          placeholder="Mit Komma getrennt eingeben"
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white">Turniererfolge</h4>
          <button
            type="button"
            onClick={addCompetition}
            className="px-3 py-1 text-sm text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
          >
            + Hinzufügen
          </button>
        </div>

        <div className="space-y-4">
          {formData.competitions.map((comp, index) => (
            <div key={index} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Datum
                  </label>
                  <input
                    type="date"
                    value={comp.date}
                    onChange={(e) => {
                      const newComps = [...formData.competitions];
                      newComps[index] = { ...comp, date: e.target.value };
                      setFormData({ ...formData, competitions: newComps });
                    }}
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Turnier
                  </label>
                  <input
                    type="text"
                    value={comp.name}
                    onChange={(e) => {
                      const newComps = [...formData.competitions];
                      newComps[index] = { ...comp, name: e.target.value };
                      setFormData({ ...formData, competitions: newComps });
                    }}
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Disziplin
                  </label>
                  <input
                    type="text"
                    value={comp.discipline}
                    onChange={(e) => {
                      const newComps = [...formData.competitions];
                      newComps[index] = { ...comp, discipline: e.target.value };
                      setFormData({ ...formData, competitions: newComps });
                    }}
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Klasse
                  </label>
                  <input
                    type="text"
                    value={comp.level}
                    onChange={(e) => {
                      const newComps = [...formData.competitions];
                      newComps[index] = { ...comp, level: e.target.value };
                      setFormData({ ...formData, competitions: newComps });
                    }}
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
                    required
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Ergebnis
                  </label>
                  <input
                    type="text"
                    value={comp.result}
                    onChange={(e) => {
                      const newComps = [...formData.competitions];
                      newComps[index] = { ...comp, result: e.target.value };
                      setFormData({ ...formData, competitions: newComps });
                    }}
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
                    required
                  />
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={() => removeCompetition(index)}
                  className="text-sm text-red-600 hover:text-red-500 dark:text-red-400 dark:hover:text-red-300"
                >
                  Entfernen
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Ziele
        </label>
        <input
          type="text"
          value={formData.goals.join(', ')}
          onChange={(e) => setFormData({
            ...formData,
            goals: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
          })}
          className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
          placeholder="Mit Komma getrennt eingeben"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Trainer
        </label>
        <input
          type="text"
          value={formData.trainer}
          onChange={(e) => setFormData({ ...formData, trainer: e.target.value })}
          className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Trainingsplan
        </label>
        <textarea
          value={formData.trainingSchedule}
          onChange={(e) => setFormData({ ...formData, trainingSchedule: e.target.value })}
          rows={4}
          className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
        />
      </div>
    </form>
  );
};